import React, { useEffect, useState } from 'react';
import ServicesDropdown from './ServicesDropdown';
import GroupOrUserSection from '../../GroupOrUserSection';
import FormGroup from '../../../generic/FormGroup';
import ButtonDefault from '../../../buttons/ButtonDefault';
import ButtonPrimary from '../../../buttons/ButtonPrimary';
import translations from '../../../../translations';
import GraphActions from '../../../../actions/graph';
import Select from 'react-select';
import useDocumentKeyDown from '../../../../utils/useDocumentKeyDown';

import { AUTHENTICATION_TYPES, SOCIAL_STREAM, keyCodes, MAP_ELEMENT_TYPES } from '../../../../constants';
import { pathOr } from 'ramda';
import { connect } from 'react-redux';
import { getByID, isNullOrEmpty, selectStyles } from '../../../../utils/core';
import { setGroupElement, saveGroupElement } from '../../../../actions/reduxActions/groupElements';
import {
    setAuthorizationAuthenticationType,
    setAuthorizationSocialStream,
    setAuthorizationUsersOrGroups,
    addAuthorizationUsersOrGroups,
    changeAuthorizationUserAttribute,
    deleteAuthorizationUser,
    changeAuthorizationGroupAttribute,
    deleteAuthorizationGroup,
    setComment,
} from '../../../../actions/reduxActions/swimlanes';

import { dismissMapElementConfig, setMapElementDeveloperName, removeMapElement } from '../../../../actions/reduxActions/mapElements';
import ConfigLayoutWrapper from '../ConfigLayoutWrapper';

/**
 * 
 * @param {Object} mapElement The raw JSON metadata for the swimlane element 
 * @param {String} id The ID of the swimlane
 * @param {Function} set Adds a groupelement to the redux store
 * @param {Number} x The X coordinate of where the swimlane is positioned on the canvas
 * @param {Number} y The Y coordinate of where the swimlane is positioned on the canvas
 * @param {String} elementType This should be "swimlane"
 * @param {String} tenantId
 * @param {String} authenticationToken
 * @param {Function} setAuthType Sets the GlobalAuthenticationType for the specific swimlane config in the store
 * @param {Function} setUsersOrGroups Updates the authorized users/groups for the specific swimlane config in the store
 * @param {Function} addUsersOrGroups Adds new users/groups for the specific swimlane config in the store
 * @param {Function} updateUserAttribute Updates a users attribute value for the specific swimlane config in the store
 * @param {Function} deleteUser Removes a user for the specific swimlane config in the store
 * @param {Function} updateGroupAttribute Updates a groups attribute value for the specific swimlane config in the store
 * @param {Function} deleteGroup Removes a group for the specific swimlane config in the store
 * @param {Function} setStreamType Sets the streamBehaviourType for the specific swimlane config in the store
 * (determines whether to display a social feed)
 * @param {Function} setName Updates the developerName for the specific swimlane config in the store
 * @param {Function} commentSet Updates the developerSummary for the specific swimlane config in the store
 * @param {String} editingToken
 * @param {String} flowId
 * @param {Function} cancel Shuts the swimlane config panel and removes the entry from the store
 * @param {Function} save Saves the swimlane config
 * @param {Function} remove Removes the swimlane from state
 * @param {String} groupElementId
 * @param {Object} container Modal container
 */
const Swimlane = ({
    mapElement,
    id,
    setGroupElement: set,
    x,
    y,
    elementType,
    tenantId,
    authenticationToken,
    setAuthType,
    setUsersOrGroups,
    addUsersOrGroups,
    updateUserAttribute,
    deleteUser,
    updateGroupAttribute,
    deleteGroup,
    setStreamType,
    setName,
    commentSet,
    editingToken,
    flowId,
    cancel,
    save,
    remove,
    groupElementId,
    container,
}) => {

    const [isNewGroup, setIsNewGroup] = useState(false);
    const [hasSubmitted, updateHasSubmitted] = useState(false);

    useEffect(() => {
        if (!mapElement) {
            setIsNewGroup(true);
            set({
                elementType,
                x,
                y,
                developerName: '',
                id,
                authorization: { streamBehaviourType: 'NONE' },
                width: 800,
                height: 200,
                groupElementId,
            });
        }
    }, []);

    const checkRequiredFields = () => ({
        defaultDeveloperName: pathOr('', ['developerName'], mapElement),
        defaultAuthType: pathOr('', ['authorization', 'globalAuthenticationType'], mapElement),
        defaultStreamType: pathOr('', ['authorization', 'streamBehaviourType'], mapElement),
        defaultServiceId: pathOr('', ['authorization', 'serviceElementId'], mapElement),
    });

    const onAuthTypeChange = ({ value }) => {
        setAuthType({ groupElement: mapElement, authenticationType: value });

        // Changing the authentication type should clear the authorized users/groups 
        setUsersOrGroups(mapElement.id, null, 'user');
        setUsersOrGroups(mapElement.id, null, 'group');
    };

    const onSocialFeedChange = ({ value }) => {
        setStreamType({ groupElement: mapElement, streamBehaviour: value });
    };

    const onDeveloperNameChange = (e) => {
        setName({ mapElement, developerName: e.target.value });
    };

    const onCommentChange = (e) => {
        commentSet({ groupElement: mapElement, comment: e.target.value });
    };

    const onSave = () => {
        updateHasSubmitted(true);

        const isFormValid = Object.values(checkRequiredFields()).every(field => !isNullOrEmpty(field));

        // Only submit the request if the client-side validation passes
        if (isFormValid) {
            save(
                mapElement,
                flowId,
                editingToken,
                tenantId,
                authenticationToken,
                cancel,
            );
        }
    };

    const onClose = () => {

        if (isNewGroup) {
            remove(mapElement.id);
        }

        // This triggers a re-render of the entire Flow canvas
        // so that changes we have made to the map element appear in the canvas
        GraphActions.get(flowId, tenantId, authenticationToken);

        // Closes the config panel
        cancel(flowId);
    };

    // For when the ESC is pressed
    useDocumentKeyDown(keyCodes.ESCAPE, onClose);

    const { defaultDeveloperName, defaultAuthType, defaultStreamType, defaultServiceId } = checkRequiredFields();
    const defaultDeveloperSummary = pathOr('', ['developerSummary'], mapElement);

    const renderBody = () => [
        <FormGroup
            label={translations.SWIMLANE_CONFIG_name_label}
            htmlFor="swimlane-name"
            isRequired
            validationMessage={translations.SWIMLANE_CONFIG_name_validation_message}
            isValid={!isNullOrEmpty(defaultDeveloperName)}
            showValidation={hasSubmitted}
        >
            <input
                id="swimlane-name"
                className="form-control form-control-width"
                value={defaultDeveloperName}
                onChange={onDeveloperNameChange}
                type="text"
            />
        </FormGroup>,
        <FormGroup
            label={translations.SWIMLANE_CONFIG_services_label}
            htmlFor="swimlane-services"
            isRequired
            validationMessage={translations.SWIMLANE_CONFIG_services_validation_message}
            isValid={!isNullOrEmpty(defaultServiceId)}
            showValidation={hasSubmitted}
        >
            <ServicesDropdown mapElement={mapElement} />
        </FormGroup>,
        <FormGroup
            label={translations.SWIMLANE_CONFIG_auth_type_label}
            htmlFor="swimlane-authentication-type"
            isRequired
            validationMessage={translations.SWIMLANE_CONFIG_auth_type_validation_message}
            isValid={!isNullOrEmpty(defaultAuthType)}
            showValidation={hasSubmitted}
        >
            <Select
                inputId="swimlane-authentication-type"
                name="swimlane-authentication-type"
                isMulti={false}
                styles={selectStyles}
                options={AUTHENTICATION_TYPES}
                onChange={onAuthTypeChange}
                placeholder="Select an Authentication Type"
                value={AUTHENTICATION_TYPES.find(at => at.value === defaultAuthType) || null}
                noOptionsMessage={() => 'No results found'}
            />
        </FormGroup>,
        <FormGroup
            label={translations.SWIMLANE_CONFIG_social_feed_label}
            htmlFor="swimlane-social-feed"
            isRequired
            validationMessage={translations.SWIMLANE_CONFIG_social_feed_validation_message}
            isValid={!isNullOrEmpty(defaultStreamType)}
            showValidation={hasSubmitted}
        >
            <Select
                inputId="swimlane-social-feed"
                name="swimlane-social-feed"
                isMulti={false}
                styles={selectStyles}
                options={SOCIAL_STREAM}
                onChange={onSocialFeedChange}
                value={SOCIAL_STREAM.find(ss => ss.value === defaultStreamType) || null}
                noOptionsMessage={() => 'No results found'}
            />
        </FormGroup>,
        <div className="authorization-config">
            {mapElement && mapElement.authorization && mapElement.authorization.globalAuthenticationType === 'SPECIFIED' ? (
                <GroupOrUserSection
                    textGroupOrUser="group"
                    elementId={id}
                    tenantId={tenantId}
                    authenticationToken={authenticationToken}
                    serviceElementId={mapElement.authorization.serviceElementId}
                    authorizationElement={mapElement}
                    setUsersOrGroups={setUsersOrGroups}
                    addUsersOrGroups={addUsersOrGroups}
                    deleteGroup={deleteGroup}
                    updateGroupAttribute={updateGroupAttribute}
                    deleteUser={deleteUser}
                    updateUserAttribute={updateUserAttribute}
                />
            ) : null}
            {mapElement && mapElement.authorization && mapElement.authorization.globalAuthenticationType === 'SPECIFIED' ? (
                <GroupOrUserSection
                    textGroupOrUser="user"
                    elementId={id}
                    tenantId={tenantId}
                    authenticationToken={authenticationToken}
                    serviceElementId={mapElement.authorization.serviceElementId}
                    authorizationElement={mapElement}
                    setUsersOrGroups={setUsersOrGroups}
                    addUsersOrGroups={addUsersOrGroups}
                    deleteGroup={deleteGroup}
                    updateGroupAttribute={updateGroupAttribute}
                    deleteUser={deleteUser}
                    updateUserAttribute={updateUserAttribute}
                />
            ) : null}
        </div>,
        <FormGroup
            label={translations.SWIMLANE_CONFIG_comment_label}
            htmlFor="swimlane-comments"
        >
            <textarea
                id="swimlane-comments"
                className="form-control form-control-width"
                value={defaultDeveloperSummary}
                onChange={onCommentChange}
            />
        </FormGroup>,
    ];

    const renderFooter = () => [
        <ButtonDefault className="flex-child-right" onClick={onClose}>
            {translations.SWIMLANE_CONFIG_cancel_button_text}
        </ButtonDefault>,
        <ButtonPrimary className="margin-left" onClick={onSave}>
            {translations.SWIMLANE_CONFIG_save_button_text}
        </ButtonPrimary>,
    ];

    const title = `${translations.SWIMLANE_CONFIG_heading} ${defaultDeveloperName}`;

    return (
        <ConfigLayoutWrapper
            renderHeader={() => <h3 className="margin-top-small">{title}</h3>}
            renderBody={renderBody}
            renderFooter={renderFooter}
            title={title}
            elementType={MAP_ELEMENT_TYPES.swimlane}
            onHide={onClose}
            container={container}
        />
    );
};

export default connect(
    ({ currentUser, groupElements }, ownProps) => ({
        mapElement: getByID(ownProps.id, groupElements),
        tenantId: currentUser.tenantId,
        authenticationToken: currentUser.authenticationToken,
    }),
    {
        setGroupElement,
        setName: setMapElementDeveloperName,
        commentSet: setComment,
        setAuthType: setAuthorizationAuthenticationType,
        setStreamType: setAuthorizationSocialStream,
        setUsersOrGroups: setAuthorizationUsersOrGroups,
        addUsersOrGroups: addAuthorizationUsersOrGroups,
        updateUserAttribute: changeAuthorizationUserAttribute,
        deleteUser: deleteAuthorizationUser,
        updateGroupAttribute: changeAuthorizationGroupAttribute,
        deleteGroup: deleteAuthorizationGroup,
        save: saveGroupElement,
        cancel: dismissMapElementConfig,
        remove: removeMapElement,
    },
)(Swimlane);
